<template>
  <div class="flex flex-col gap-2.5 h-full overflow-y-auto pr-1">
    <!-- 1. General Presentation Settings Section (Collapsible - Closed by default) -->
    <div class="flex flex-col pb-2.5 border-b border-gray-200 dark:border-gray-700 px-1">
      <div
        class="flex items-center justify-between cursor-pointer select-none py-1 group"
        @click="isGeneralOpen = !isGeneralOpen"
      >
        <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
          <Cog6ToothIcon class="w-3.5 h-3.5 text-indigo-500" />
          {{ $t('presentation.settings') }}
        </span>
        <button type="button" class="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" :title="isGeneralOpen ? ($t('presentation.collapse') || 'Collapse') : ($t('presentation.expand') || 'Expand')">
          <ChevronUpIcon v-if="isGeneralOpen" class="w-3.5 h-3.5" />
          <ChevronDownIcon v-else class="w-3.5 h-3.5" />
        </button>
      </div>

      <div v-show="isGeneralOpen" class="flex flex-col gap-2.5 pt-2">
        <TriadSelect
          :modelValue="props.screen"
          :options="screenOptions"
          :label="$t('presentation.screen')"
          @change="$emit('update:screen', $event)"
          root-class="block w-full"
        />

        <TriadSelect
          v-if="props.linesOptions && props.linesOptions.length > 0"
          :modelValue="String(props.linesPerSlide)"
          :options="props.linesOptions"
          :label="$t('presentation.linesPerSlide')"
          @change="$emit('update:lines-per-slide', $event)"
          root-class="block w-full"
        />

        <TriadSelect
          v-if="props.templateOptions && props.templateOptions.length > 0"
          :modelValue="props.selectedTemplate"
          :options="props.templateOptions"
          :label="$t('presentation.template')"
          @change="$emit('update:template', $event)"
          root-class="block w-full"
        />

        <TriadSelect
          v-if="props.templateTargetOptions && props.templateTargetOptions.length > 0"
          :modelValue="props.templateTarget"
          :options="props.templateTargetOptions"
          :label="$t('presentation.applyTarget')"
          @change="$emit('update:template-target', $event)"
          root-class="block w-full"
        />

        <!-- Logo Setting -->
        <div class="pt-2 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-1.5">
          <label class="text-[10px] font-semibold text-gray-600 dark:text-gray-300">
            {{ $t('presentation.logoSettings') }}
          </label>
          <div v-if="props.logoUrl" class="flex items-center gap-2 p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
            <img :src="props.logoUrl" class="w-8 h-8 object-contain rounded bg-black/20 shrink-0" alt="Logo" />
            <div class="flex-1 min-w-0">
              <span class="text-[10px] text-gray-500 dark:text-gray-400 truncate block">{{ $t('presentation.logoConfigured') }}</span>
            </div>
            <button
              type="button"
              @click="$emit('remove-logo')"
              class="p-1 text-gray-400 hover:text-rose-500 rounded transition-colors"
              :title="$t('presentation.removeLogo')"
            >
              <TrashIcon class="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            v-else
            type="button"
            @click="$emit('open-logo-upload')"
            class="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 rounded-lg border border-indigo-200 dark:border-indigo-800/60 transition-colors"
          >
            <PhotoIcon class="w-3.5 h-3.5" />
            <span>{{ $t('presentation.uploadLogo') }}</span>
          </button>
        </div>

        <!-- Slide Effects & Transition Settings -->
        <div class="pt-2 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-xs font-semibold text-gray-700 dark:text-gray-200">
              {{ $t('presentation.slideEffects') }}
            </span>
            <label class="relative inline-flex items-center cursor-pointer" :title="$t('presentation.slideEffects')">
              <input
                type="checkbox"
                class="sr-only peer"
                :checked="props.effects !== false"
                @change="$emit('update:effects', $event.target.checked)"
              />
              <div class="w-8 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
            </label>
          </div>

          <div v-show="props.effects !== false" class="flex flex-col gap-2">
            <TriadSelect
              :modelValue="props.effectType || 'fade'"
              :options="slideTransitionOptions"
              :label="$t('presentation.slideTransition')"
              @change="$emit('update:effect-type', $event)"
              root-class="block w-full"
            />

            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-gray-600 dark:text-gray-300">
                {{ $t('presentation.effectDuration') }}
              </label>
              <div class="flex items-center gap-2">
                <input
                  type="number"
                  min="0.1"
                  max="5"
                  step="0.1"
                  :value="((props.effectDuration ?? 500) / 1000).toFixed(1)"
                  @input="onEffectDurationInput($event)"
                  class="w-full text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:border-indigo-500 focus:outline-none font-mono"
                />
                <span class="text-xs text-gray-500 font-mono shrink-0">s</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="props.pageEditable" class="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-800">
          <span class="text-xs font-medium text-gray-600 dark:text-gray-300">
            {{ $t('presentation.pageEffect') }}
          </span>
          <label
            class="relative inline-flex items-center cursor-pointer"
            :title="$t('presentation.pageEffect')"
          >
            <input
              type="checkbox"
              class="sr-only peer"
              :checked="props.pageEffect"
              @change="$emit('toggle-page-effect')"
            />
            <div class="w-8 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
          </label>
        </div>
      </div>
    </div>

    <!-- 2. Continuous Song Video Section (Collapsible - Closed by default) -->
    <div class="flex flex-col pb-2.5 border-b border-gray-200 dark:border-gray-700 px-1">
      <div
        class="flex items-center justify-between cursor-pointer select-none py-1 group"
        @click="isSongVideoOpen = !isSongVideoOpen"
      >
        <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
          <PlayCircleIcon class="w-3.5 h-3.5 text-indigo-500" />
          {{ $t('presentation.songVideo') }}
        </span>
        <div class="flex items-center gap-1">
          <span
            v-if="props.songVideo?.videoId"
            class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"
            :title="$t('presentation.songVideoContinuous')"
          ></span>
          <button type="button" class="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" :title="isSongVideoOpen ? ($t('presentation.collapse') || 'Collapse') : ($t('presentation.expand') || 'Expand')">
            <ChevronUpIcon v-if="isSongVideoOpen" class="w-3.5 h-3.5" />
            <ChevronDownIcon v-else class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div v-show="isSongVideoOpen" class="flex flex-col gap-2 pt-2">
        <div v-if="props.songVideo?.videoId" class="flex flex-col gap-2 bg-slate-50 dark:bg-gray-800/60 p-2 rounded-lg border border-slate-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 min-w-0">
              <img
                :src="songVideoThumb"
                class="w-14 h-9 object-cover rounded bg-black shrink-0"
                alt=""
              />
              <div class="flex-1 min-w-0">
                <p class="text-[11px] font-mono text-gray-500 dark:text-gray-400 truncate">
                  {{ props.songVideo.videoId }}
                </p>
                <p class="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                  {{ $t('presentation.songVideoContinuous') }}
                </p>
              </div>
            </div>
            <button
              type="button"
              @click="clearSongVideo"
              class="p-1 text-gray-400 hover:text-rose-500 rounded transition-colors shrink-0"
              :title="$t('presentation.removeSongVideo')"
            >
              <TrashIcon class="w-3.5 h-3.5" />
            </button>
          </div>

          <input
            type="text"
            :value="getYouTubeUrl(props.songVideo.url || props.songVideo.videoId)"
            @input="onSongVideoUrlChange($event)"
            placeholder="https://www.youtube.com/watch?v=..."
            class="w-full text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:border-indigo-500 focus:outline-none"
          />

          <div class="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-300 pt-0.5">
            <span>{{ $t('presentation.videoLoop') }}</span>
            <label class="relative inline-flex items-center cursor-pointer" :title="$t('presentation.videoLoop')">
              <input
                type="checkbox"
                class="sr-only peer"
                :checked="props.songVideo.loop !== false"
                @change="onSongVideoLoopChange($event)"
              />
              <div class="w-7 h-3.5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
            </label>
          </div>

          <div class="grid grid-cols-2 gap-1.5 pt-0.5">
            <div class="flex flex-col gap-0.5">
              <label class="text-[10px] text-gray-500 dark:text-gray-400">{{ $t('presentation.videoStart') }}</label>
              <input
                type="number"
                min="0"
                step="1"
                :value="props.songVideo.start ?? 0"
                @input="onSongVideoStartChange($event)"
                class="w-full text-xs px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:border-indigo-500 focus:outline-none font-mono"
              />
            </div>
            <div class="flex flex-col gap-0.5">
              <label class="text-[10px] text-gray-500 dark:text-gray-400">{{ $t('presentation.videoEnd') }}</label>
              <input
                type="number"
                min="0"
                step="1"
                :value="props.songVideo.end ?? ''"
                :placeholder="$t('presentation.videoEndPlaceholder')"
                @input="onSongVideoEndChange($event)"
                class="w-full text-xs px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:border-indigo-500 focus:outline-none font-mono"
              />
            </div>
          </div>

          <div class="flex flex-col gap-0.5 pt-0.5">
            <TriadSelect
              :modelValue="props.songVideo.transition || 'fade'"
              :options="transitionOptions"
              :label="$t('presentation.videoTransition')"
              @change="onSongVideoTransitionChange($event)"
              root-class="block w-full"
            />
          </div>
        </div>

        <div v-else class="flex flex-col gap-1.5">
          <div class="flex gap-1.5">
            <input
              type="text"
              :value="newSongVideoUrl"
              @input="onNewSongVideoInput($event)"
              @keydown.enter="submitNewSongVideo"
              :placeholder="$t('presentation.addSongVideoPlaceholder')"
              class="flex-1 min-w-0 text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:border-indigo-500 focus:outline-none"
            />
            <button
              type="button"
              @click="submitNewSongVideo"
              class="px-2.5 py-1 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shrink-0 shadow-sm"
              :title="$t('presentation.addSongVideo')"
            >
              OK
            </button>
          </div>
          <p class="text-[10px] text-gray-400 dark:text-gray-500 leading-tight">
            {{ $t('presentation.songVideoDesc') }}
          </p>
        </div>
      </div>
    </div>

    <!-- 3. Layers Section (Collapsible - OPEN by default) -->
    <div class="flex flex-col flex-1 min-h-[140px] px-1">
      <div class="flex items-center justify-between select-none py-1">
        <div
          class="flex items-center gap-1.5 cursor-pointer group flex-1 min-w-0"
          @click="isLayersOpen = !isLayersOpen"
        >
          <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
            <QueueListIcon class="w-3.5 h-3.5 text-indigo-500" />
            {{ $t('presentation.layers') }}
          </span>
        </div>

        <div class="flex items-center gap-1 shrink-0">
          <!-- Layer Add Action Buttons -->
          <button
            type="button"
            @click.stop="handleAddText"
            class="p-1 rounded text-slate-500 hover:text-slate-700 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
            :title="$t('presentation.addTextBox')"
          >
            <span class="block text-xs font-black leading-none px-0.5">T+</span>
          </button>
          <button
            type="button"
            @click.stop="handleAddImage"
            class="p-1 rounded text-slate-500 hover:text-slate-700 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
            :title="$t('presentation.addImageLayer')"
          >
            <PhotoIcon class="w-4 h-4" />
          </button>
          <button
            type="button"
            @click.stop="handleAddVideo"
            class="p-1 rounded text-slate-500 hover:text-slate-700 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
            :title="$t('presentation.addVideoLayer')"
          >
            <PlayIcon class="w-4 h-4" />
          </button>
          <button
            type="button"
            @click.stop="$emit('replicate-layers')"
            class="p-1 rounded text-slate-500 hover:text-indigo-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
            :title="$t('presentation.replicateLayers')"
          >
            <DocumentDuplicateIcon class="w-4 h-4" />
          </button>

          <!-- Collapse Button at the far right -->
          <button
            type="button"
            @click="isLayersOpen = !isLayersOpen"
            :title="isLayersOpen ? ($t('presentation.collapse') || 'Collapse') : ($t('presentation.expand') || 'Expand')"
            class="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors ml-0.5"
          >
            <ChevronUpIcon v-if="isLayersOpen" class="w-3.5 h-3.5" />
            <ChevronDownIcon v-else class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div v-show="isLayersOpen" class="flex flex-col flex-1 min-h-0 pt-1">
        <!-- Group Filter Tabs -->
        <div class="flex items-center justify-between p-0.5 mb-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <button
            type="button"
            @click="activeGroupFilter = 'all'"
            class="flex-1 py-1 text-[10px] font-semibold rounded-md transition-all text-center"
            :class="activeGroupFilter === 'all'
              ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-300 shadow-xs'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
          >
            {{ $t('presentation.allGroups') || 'Todos' }}
          </button>
          <button
            type="button"
            @click="activeGroupFilter = 'primary'"
            class="flex-1 py-1 text-[10px] font-semibold rounded-md transition-all text-center flex items-center justify-center gap-1"
            :class="activeGroupFilter === 'primary'
              ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-300 shadow-xs'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
            {{ $t('presentation.groupPrimary') || 'Primário' }}
          </button>
          <button
            type="button"
            @click="activeGroupFilter = 'secondary'"
            class="flex-1 py-1 text-[10px] font-semibold rounded-md transition-all text-center flex items-center justify-center gap-1"
            :class="activeGroupFilter === 'secondary'
              ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-300 shadow-xs'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
            {{ $t('presentation.groupSecondary') || 'Secundário' }}
          </button>
        </div>

        <p v-if="filteredDisplayLayers.length === 0" class="px-1 py-2 text-[11px] text-gray-400 italic">
          {{ $t('presentation.noLayers') }}
        </p>

        <div ref="listRef" class="flex-1 overflow-y-auto px-1 py-0.5 -mx-1">
          <div
            v-for="layer in filteredDisplayLayers"
            :key="layer.id"
            @click="$emit('select-layer', layer.id, $event)"
            class="flex items-center gap-1.5 px-1.5 py-1 rounded-lg cursor-pointer transition-colors"
            :class="isSelected(layer.id) ? 'bg-indigo-50 dark:bg-indigo-900/40 ring-1 ring-indigo-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700'"
          >
            <span class="cursor-grab active:cursor-grabbing text-gray-400 shrink-0" data-layer-handle="true" :title="$t('presentation.dragToReorder') || 'Drag to reorder'">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h.008v.008H8.25V6.75zm0 3h.008v.008H8.25V9.75zm0 3h.008v.008H8.25v-.008zm0 3h.008v.008H8.25v-.008zm3-9h.008v.008H11.25V6.75zm0 3h.008v.008H11.25V9.75zm0 3h.008v.008H11.25v-.008zm0 3h.008v.008H11.25v-.008zm3-9h.008v.008H14.25V6.75zm0 3h.008v.008H14.25V9.75zm0 3h.008v.008H14.25v-.008zm0 3h.008v.008H14.25v-.008z" />
              </svg>
            </span>
            <span class="shrink-0 text-gray-400">
              <PhotoIcon v-if="layer.type === 'image'" class="w-4 h-4" />
              <PlayIcon v-else-if="layer.type === 'video'" class="w-4 h-4" />
              <span v-else class="block text-xs font-black leading-none px-0.5">T</span>
            </span>
            <span v-if="editingId !== layer.id" class="flex-1 min-w-0 truncate text-xs font-medium text-gray-700 dark:text-gray-200" @dblclick="startRename(layer)">
              {{ layer.name || layer.id }}
            </span>
            <input
              v-else
              :value="layer.name"
              @blur="finishRename(layer, $event)"
              @keydown.enter="finishRename(layer, $event)"
              @keydown.escape="editingId = null"
              @click.stop
              class="flex-1 min-w-0 text-xs px-1 py-0.5 rounded border border-indigo-400 focus:outline-none bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
            />
            <!-- Group Badge / Toggle Button -->
            <button
              type="button"
              @click.stop="toggleLayerGroup(layer)"
              class="px-1.5 py-0.5 text-[9px] font-extrabold rounded tracking-wider transition-all shrink-0"
              :class="(layer.group || 'primary') === 'secondary'
                ? 'bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900 border border-purple-300 dark:border-purple-700'
                : 'bg-blue-100 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900 border border-blue-300 dark:border-blue-700'"
              :title="((layer.group || 'primary') === 'secondary' ? ($t('presentation.groupSecondary') || 'Secundário') : ($t('presentation.groupPrimary') || 'Primário')) + ' - ' + ($t('presentation.groupDesc') || 'Grupo')"
            >
              {{ (layer.group || 'primary') === 'secondary' ? 'S' : 'P' }}
            </button>
            <button
              type="button"
              @click.stop="$emit('toggle-visibility', layer.id)"
              class="p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              :title="$t('presentation.toggleVisibility')"
            >
              <EyeSlashIcon v-if="layer.visible === false" class="w-4 h-4" />
              <EyeIcon v-else class="w-4 h-4" />
            </button>
            <button
              type="button"
              @click.stop="$emit('toggle-lock', layer.id)"
              class="p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              :class="{ 'text-amber-500': layer.locked }"
              :title="$t('presentation.toggleLock')"
            >
              <LockClosedIcon v-if="layer.locked" class="w-4 h-4" />
              <LockOpenIcon v-else class="w-4 h-4" />
            </button>
            <button
              type="button"
              @click.stop="$emit('delete-layer', layer.id)"
              class="p-0.5 rounded text-gray-400 hover:text-rose-500"
              :title="$t('presentation.deleteLayer')"
            >
              <TrashIcon class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Video Configuration Section (Collapsible - visible when a video layer is selected) -->
    <div
      v-if="selectedVideoLayer"
      class="flex flex-col pb-2.5 border-t border-gray-200 dark:border-gray-700 px-1"
    >
      <div
        class="flex items-center justify-between cursor-pointer select-none py-1 group"
        @click="isVideoConfigOpen = !isVideoConfigOpen"
      >
        <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
          <PlayIcon class="w-3.5 h-3.5 text-indigo-500" />
          {{ $t('presentation.videoSettings') || 'Configurar Vídeo' }}
        </span>
        <div class="flex items-center gap-1">
          <span class="text-[10px] text-gray-500 dark:text-gray-400 font-medium truncate max-w-[80px]">
            {{ selectedVideoLayer.name }}
          </span>
          <button type="button" class="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" :title="isVideoConfigOpen ? ($t('presentation.collapse') || 'Collapse') : ($t('presentation.expand') || 'Expand')">
            <ChevronUpIcon v-if="isVideoConfigOpen" class="w-3.5 h-3.5" />
            <ChevronDownIcon v-else class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div v-show="isVideoConfigOpen" class="flex flex-col gap-2.5 pt-2">
        <!-- Video URL / Link Input -->
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-600 dark:text-gray-300 font-medium">
            {{ $t('presentation.videoUrl') || 'Link do Vídeo' }}
          </label>
          <input
            type="text"
            :value="getYouTubeUrl(selectedVideoLayer.url || selectedVideoLayer.videoId)"
            @input="onVideoUrlChange($event)"
            placeholder="https://www.youtube.com/watch?v=..."
            class="w-full text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:border-indigo-500 focus:outline-none"
          />
        </div>

        <!-- Loop Toggle -->
        <div class="flex items-center justify-between pt-1">
          <span class="text-xs text-gray-600 dark:text-gray-300 font-medium">
            {{ $t('presentation.videoLoop') || 'Repetir em loop' }}
          </span>
          <label class="relative inline-flex items-center cursor-pointer" :title="$t('presentation.videoLoop')">
            <input
              type="checkbox"
              class="sr-only peer"
              :checked="!!selectedVideoLayer.loop"
              @change="onVideoLoopChange($event)"
            />
            <div class="w-8 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
          </label>
        </div>

        <!-- Start & End Seconds (Time Range) -->
        <div class="grid grid-cols-2 gap-2 pt-1">
          <div class="flex flex-col gap-1">
            <label class="text-[11px] text-gray-600 dark:text-gray-300">
              {{ $t('presentation.videoStart') || 'Início (seg)' }}
            </label>
            <input
              type="number"
              min="0"
              step="1"
              :value="selectedVideoLayer.start ?? 0"
              @input="onVideoStartChange($event)"
              class="w-full text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:border-indigo-500 focus:outline-none font-mono"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[11px] text-gray-600 dark:text-gray-300">
              {{ $t('presentation.videoEnd') || 'Fim (seg)' }}
            </label>
            <input
              type="number"
              min="0"
              step="1"
              :value="selectedVideoLayer.end ?? ''"
              :placeholder="$t('presentation.videoEndPlaceholder') || 'Fim'"
              @input="onVideoEndChange($event)"
              class="w-full text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:border-indigo-500 focus:outline-none font-mono"
            />
          </div>
        </div>

        <!-- Promote to Continuous Song Video Button -->
        <div class="pt-2 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-1">
          <button
            type="button"
            @click="makeCurrentVideoSongVideo"
            class="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 rounded-lg border border-indigo-200 dark:border-indigo-800/60 transition-colors"
            :title="$t('presentation.songVideoDesc')"
          >
            <PlayCircleIcon class="w-4 h-4" />
            <span>{{ $t('presentation.makeSongVideo') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 5. Layer Effects Section (Collapsible - visible for ANY selected layer) -->
    <div
      v-if="selectedLayer"
      class="flex flex-col pb-2.5 border-t border-gray-200 dark:border-gray-700 px-1"
    >
      <div
        class="flex items-center justify-between cursor-pointer select-none py-1 group"
        @click="isLayerEffectsOpen = !isLayerEffectsOpen"
      >
        <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
          <PhotoIcon v-if="selectedLayer.type === 'image'" class="w-3.5 h-3.5 text-indigo-500" />
          <PlayIcon v-else-if="selectedLayer.type === 'video'" class="w-3.5 h-3.5 text-indigo-500" />
          <AdjustmentsHorizontalIcon v-else class="w-3.5 h-3.5 text-indigo-500" />
          {{ $t('presentation.layerEffects') || 'Efeitos da Camada' }}
        </span>
        <div class="flex items-center gap-1">
          <span class="text-[10px] text-gray-500 dark:text-gray-400 font-medium truncate max-w-[80px]">
            {{ selectedLayer.name }}
          </span>
          <button type="button" class="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" :title="isLayerEffectsOpen ? ($t('presentation.collapse') || 'Collapse') : ($t('presentation.expand') || 'Expand')">
            <ChevronUpIcon v-if="isLayerEffectsOpen" class="w-3.5 h-3.5" />
            <ChevronDownIcon v-else class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div v-show="isLayerEffectsOpen" class="flex flex-col gap-2.5 pt-2">
        <!-- Layer Group Selector -->
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-600 dark:text-gray-300 font-medium">
            {{ $t('presentation.group') || 'Grupo da Camada' }}
          </label>
          <div class="grid grid-cols-2 gap-1 p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              @click="onGroupChange('primary')"
              class="py-1 px-2 text-xs font-semibold rounded-md transition-all text-center flex items-center justify-center gap-1.5"
              :class="(selectedLayer.group || 'primary') === 'primary'
                ? 'bg-white dark:bg-indigo-600 text-indigo-600 dark:text-white shadow-xs'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
            >
              <span class="w-2 h-2 rounded-full bg-blue-500 shrink-0"></span>
              {{ $t('presentation.groupPrimary') || 'Primário' }}
            </button>
            <button
              type="button"
              @click="onGroupChange('secondary')"
              class="py-1 px-2 text-xs font-semibold rounded-md transition-all text-center flex items-center justify-center gap-1.5"
              :class="selectedLayer.group === 'secondary'
                ? 'bg-white dark:bg-indigo-600 text-indigo-600 dark:text-white shadow-xs'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
            >
              <span class="w-2 h-2 rounded-full bg-purple-500 shrink-0"></span>
              {{ $t('presentation.groupSecondary') || 'Secundário' }}
            </button>
          </div>
        </div>

        <!-- Opacity Slider -->
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
            <span>{{ $t('presentation.opacity') || 'Opacidade' }}</span>
            <span class="font-mono text-gray-400 text-[11px]">{{ Math.round((selectedLayer.opacity ?? 1) * 100) }}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            :value="Math.round((selectedLayer.opacity ?? 1) * 100)"
            @input="onOpacityChange($event)"
            class="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>

        <!-- Blur Slider -->
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
            <span>{{ $t('presentation.blur') || 'Blur' }}</span>
            <span class="font-mono text-gray-400 text-[11px]">{{ selectedLayer.blur ?? 0 }}px</span>
          </div>
          <input
            type="range"
            min="0"
            max="20"
            step="1"
            :value="selectedLayer.blur ?? 0"
            @input="onBlurChange($event)"
            class="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>

        <!-- Border Radius Slider -->
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
            <span>{{ $t('presentation.borderRadius') || 'Bordas' }}</span>
            <span class="font-mono text-gray-400 text-[11px]">{{ selectedLayer.borderRadius ?? 0 }}px</span>
          </div>
          <input
            type="range"
            min="0"
            max="40"
            step="1"
            :value="selectedLayer.borderRadius ?? 0"
            @input="onRadiusChange($event)"
            class="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Sortable from 'sortablejs';
import { PhotoIcon, PlayIcon, PlayCircleIcon, EyeIcon, EyeSlashIcon, LockClosedIcon, LockOpenIcon, TrashIcon, DocumentDuplicateIcon, AdjustmentsHorizontalIcon, Cog6ToothIcon, ChevronDownIcon, ChevronUpIcon, QueueListIcon } from '@heroicons/vue/24/outline';
import TriadSelect from '@/components/common/TriadSelect.vue';
import { getVideoId, getThumbnailUrl, getYouTubeUrl } from '@/utils/youtube';

const { t } = useI18n();

// Collapsible section state: only Layers section open by default
const isGeneralOpen = ref(false);
const isSongVideoOpen = ref(false);
const isLayersOpen = ref(true);
const isVideoConfigOpen = ref(true);
const isLayerEffectsOpen = ref(true);

const props = defineProps({
  layers: { type: Array, default: () => [] },
  selectedId: { type: String, default: null },
  selectedIds: { type: Array, default: () => [] },
  screen: { type: String, default: '4:3' },
  linesPerSlide: { type: [String, Number], default: '2' },
  linesOptions: { type: Array, default: () => [] },
  selectedTemplate: { type: String, default: 'standard' },
  templateOptions: { type: Array, default: () => [] },
  templateTarget: { type: String, default: 'all' },
  templateTargetOptions: { type: Array, default: () => [] },
  logoUrl: { type: String, default: null },
  effects: { type: Boolean, default: true },
  effectType: { type: String, default: 'fade' },
  effectDuration: { type: Number, default: 500 },
  pageEffect: { type: Boolean, default: true },
  pageEditable: { type: Boolean, default: true },
  songVideo: { type: Object, default: null },
});

const emit = defineEmits([
  'select-layer', 'toggle-visibility', 'toggle-lock', 'delete-layer',
  'rename-layer', 'reorder-layers', 'add-text', 'add-image', 'add-video', 'replicate-layers',
  'update:screen', 'update:lines-per-slide', 'update:template', 'update:template-target',
  'update:effects', 'update:effect-type', 'update:effect-duration',
  'open-logo-upload', 'remove-logo', 'toggle-page-effect', 'update-layer-style', 'update:song-video',
]);

const slideTransitionOptions = computed(() => [
  { value: 'fade', label: t('presentation.transFade') || 'Fade' },
  { value: 'crossfade', label: t('presentation.transCrossfade') || 'Crossfade' },
  { value: 'slide-left', label: t('presentation.transSlideLeft') || 'Slide Left' },
  { value: 'slide-right', label: t('presentation.transSlideRight') || 'Slide Right' },
  { value: 'slide-up', label: t('presentation.transSlideUp') || 'Slide Up' },
  { value: 'slide-down', label: t('presentation.transSlideDown') || 'Slide Down' },
  { value: 'zoom', label: t('presentation.transZoom') || 'Zoom' },
  { value: 'blur', label: t('presentation.transBlur') || 'Blur' },
  { value: 'none', label: t('presentation.transNone') || 'None' },
]);

const onEffectDurationInput = (e) => {
  const seconds = parseFloat(e.target.value);
  if (!isNaN(seconds) && seconds >= 0) {
    emit('update:effect-duration', Math.round(seconds * 1000));
  }
};

const selectedLayer = computed(() => {
  const list = props.layers || [];
  return list.find((l) => (l.id === props.selectedId || (props.selectedIds || []).includes(l.id))) || null;
});

const selectedVideoLayer = computed(() => {
  return selectedLayer.value?.type === 'video' ? selectedLayer.value : null;
});

const onOpacityChange = (e) => {
  if (!selectedLayer.value) return;
  const val = Number(e.target.value) / 100;
  emit('update-layer-style', { id: selectedLayer.value.id, patch: { opacity: val } });
};

const onBlurChange = (e) => {
  if (!selectedLayer.value) return;
  const val = Number(e.target.value);
  emit('update-layer-style', { id: selectedLayer.value.id, patch: { blur: val } });
};

const onRadiusChange = (e) => {
  if (!selectedLayer.value) return;
  const val = Number(e.target.value);
  emit('update-layer-style', { id: selectedLayer.value.id, patch: { borderRadius: val } });
};

const onVideoUrlChange = (e) => {
  if (!selectedVideoLayer.value) return;
  const raw = (e.target.value || '').trim();
  const vid = getVideoId(raw) || (raw.length === 11 ? raw : '');
  const url = vid ? getYouTubeUrl(vid) : raw;
  emit('update-layer-style', {
    id: selectedVideoLayer.value.id,
    patch: { url, videoId: vid || raw },
  });
};

const onVideoLoopChange = (e) => {
  if (!selectedVideoLayer.value) return;
  emit('update-layer-style', {
    id: selectedVideoLayer.value.id,
    patch: { loop: !!e.target.checked },
  });
};

const onVideoStartChange = (e) => {
  if (!selectedVideoLayer.value) return;
  const val = e.target.value === '' ? 0 : Math.max(0, Number(e.target.value));
  emit('update-layer-style', {
    id: selectedVideoLayer.value.id,
    patch: { start: val },
  });
};

const onVideoEndChange = (e) => {
  if (!selectedVideoLayer.value) return;
  const val = e.target.value === '' ? null : Math.max(0, Number(e.target.value));
  emit('update-layer-style', {
    id: selectedVideoLayer.value.id,
    patch: { end: val },
  });
};

const makeCurrentVideoSongVideo = () => {
  if (!selectedVideoLayer.value) return;
  const l = selectedVideoLayer.value;
  const vid = getVideoId(l.videoId) || getVideoId(l.url) || (l.videoId?.length === 11 ? l.videoId : '') || '';
  const url = vid ? getYouTubeUrl(vid) : (l.url || '');
  if (vid) {
    emit('update:song-video', {
      url,
      videoId: vid,
      loop: l.loop !== false,
      start: l.start ?? 0,
      end: l.end ?? null,
    });
  }
};

const newSongVideoUrl = ref('');

const onNewSongVideoInput = (e) => {
  const raw = (e.target.value || '').trim();
  newSongVideoUrl.value = raw;
  const vid = getVideoId(raw) || (raw.length === 11 ? raw : '');
  if (vid) {
    emit('update:song-video', {
      url: getYouTubeUrl(vid),
      videoId: vid,
      loop: true,
      start: 0,
      end: null,
    });
    newSongVideoUrl.value = '';
  }
};

const submitNewSongVideo = () => {
  const raw = (newSongVideoUrl.value || '').trim();
  const vid = getVideoId(raw) || (raw.length === 11 ? raw : '');
  if (vid) {
    emit('update:song-video', {
      url: getYouTubeUrl(vid),
      videoId: vid,
      loop: true,
      start: 0,
      end: null,
    });
    newSongVideoUrl.value = '';
  }
};

const onSongVideoUrlChange = (e) => {
  const raw = (e.target.value || '').trim();
  const vid = getVideoId(raw) || (raw.length === 11 ? raw : '');
  const url = vid ? getYouTubeUrl(vid) : raw;
  emit('update:song-video', {
    ...(props.songVideo || {}),
    url,
    videoId: vid || raw,
  });
};

const onSongVideoLoopChange = (e) => {
  emit('update:song-video', {
    ...(props.songVideo || {}),
    loop: !!e.target.checked,
  });
};

const onSongVideoStartChange = (e) => {
  const val = e.target.value === '' ? 0 : Math.max(0, Number(e.target.value));
  emit('update:song-video', {
    ...(props.songVideo || {}),
    start: val,
  });
};

const onSongVideoEndChange = (e) => {
  const val = e.target.value === '' ? null : Math.max(0, Number(e.target.value));
  emit('update:song-video', {
    ...(props.songVideo || {}),
    end: val,
  });
};

const transitionOptions = computed(() => [
  { value: 'fade', label: t('presentation.transFade') || 'Fade' },
  { value: 'crossfade', label: t('presentation.transCrossfade') || 'Crossfade' },
  { value: 'slide-left', label: t('presentation.transSlideLeft') || 'Slide Left' },
  { value: 'slide-right', label: t('presentation.transSlideRight') || 'Slide Right' },
  { value: 'zoom', label: t('presentation.transZoom') || 'Zoom' },
  { value: 'none', label: t('presentation.transNone') || 'None' },
]);

const onSongVideoTransitionChange = (val) => {
  emit('update:song-video', {
    ...(props.songVideo || {}),
    transition: val,
  });
};

const clearSongVideo = () => {
  emit('update:song-video', null);
};

const songVideoThumb = computed(() => {
  if (!props.songVideo?.videoId) return '';
  try {
    return getThumbnailUrl(props.songVideo.videoId, 'hqdefault');
  } catch {
    return `https://i.ytimg.com/vi/${props.songVideo.videoId}/hqdefault.jpg`;
  }
});

const screenOptions = [
  { value: '4:3', label: '4:3' },
  { value: '16:9', label: '16:9' },
  { value: '16:10', label: '16:10' },
];

const isSelected = (id) => id === props.selectedId || (props.selectedIds || []).includes(id);

const activeGroupFilter = ref('all');

// Photoshop exibe o topo da pilha primeiro (último = frente)
const displayLayers = computed(() => [...(props.layers || [])].reverse());

const filteredDisplayLayers = computed(() => {
  const list = displayLayers.value;
  if (activeGroupFilter.value === 'all') return list;
  return list.filter((l) => (l.group || 'primary') === activeGroupFilter.value);
});

const toggleLayerGroup = (layer) => {
  if (!layer) return;
  const current = layer.group || 'primary';
  const next = current === 'primary' ? 'secondary' : 'primary';
  emit('update-layer-style', { id: layer.id, patch: { group: next } });
};

const onGroupChange = (group) => {
  if (!selectedLayer.value) return;
  emit('update-layer-style', { id: selectedLayer.value.id, patch: { group } });
};

const handleAddText = () => {
  const grp = activeGroupFilter.value === 'secondary' ? 'secondary' : 'primary';
  emit('add-text', grp);
};

const handleAddImage = () => {
  const grp = activeGroupFilter.value === 'secondary' ? 'secondary' : 'primary';
  emit('add-image', grp);
};

const handleAddVideo = () => {
  const grp = activeGroupFilter.value === 'secondary' ? 'secondary' : 'primary';
  emit('add-video', grp);
};

const editingId = ref(null);
const listRef = ref(null);
let sortable = null;

const startRename = (layer) => {
  editingId.value = layer.id;
};

const finishRename = (layer, event) => {
  const name = (event.target.value || '').trim();
  editingId.value = null;
  if (name && name !== layer.name) {
    emit('rename-layer', { id: layer.id, name });
  }
};

const initSortable = () => {
  if (sortable) sortable.destroy();
  nextTick(() => {
    if (!listRef.value) return;
    sortable = Sortable.create(listRef.value, {
      animation: 150,
      handle: '[data-layer-handle]',
      onEnd: (evt) => {
        const movedLayer = filteredDisplayLayers.value[evt.oldIndex];
        const targetLayer = filteredDisplayLayers.value[evt.newIndex];
        if (!movedLayer || !targetLayer) return;
        const oldIdx = (props.layers || []).findIndex((l) => l.id === movedLayer.id);
        const targetIdx = (props.layers || []).findIndex((l) => l.id === targetLayer.id);
        if (oldIdx !== -1 && targetIdx !== -1) {
          emit('reorder-layers', { oldIndex: oldIdx, newIndex: targetIdx });
        }
      },
    });
  });
};

watch(() => props.layers, () => initSortable(), { deep: true });
watch(activeGroupFilter, () => initSortable());
onMounted(() => initSortable());
onBeforeUnmount(() => {
  if (sortable) sortable.destroy();
});
</script>
